import {
  COMBAT,
  FLEET,
  SHIP_CLASSES,
  SHIP_CLASS_IDS,
  SHIP_COMBAT,
  type ShipClassId,
  type StanceId,
} from '@sd/content';
import { ORIGIN, hexDistance, hexKey, hexNeighbors, type Hex } from './hex';
import type { ScanState } from './exploration';
import type { PlanetState } from './planet';
import type { StarSystem } from './system';

/**
 * Ships and fleets (GDD §8). A fleet is a group of ships with one order;
 * it moves one hex at a time at the speed of its slowest ship. Pure
 * functions only.
 */
export interface ShipState {
  readonly id: number;
  readonly cls: ShipClassId;
  /**
   * Its number among its owner's ships of its class (#154): «Корвет», «Корвет 2». The
   * lowest number no living ship of the class has; absent = 1.
   */
  readonly n?: number;
  /** Remaining hull; absent = undamaged. */
  readonly hull?: number;
}

/** The number a new ship of `cls` takes: the lowest its owner's living ships leave free. */
export function freeShipNumber(fleets: readonly FleetState[], owner: string, cls: ShipClassId) {
  const taken = new Set(
    fleets.flatMap((f) =>
      f.owner === owner ? f.ships.filter((s) => s.cls === cls).map((s) => s.n ?? 1) : [],
    ),
  );
  let n = 1;
  while (taken.has(n)) n++;
  return n;
}

/** The ship a fleet goes by (#154): its heaviest, the oldest of equals. */
export function leadShip(f: FleetState): ShipState {
  return f.ships.reduce((a, s) =>
    SHIP_COMBAT[s.cls].hull > SHIP_COMBAT[a.cls].hull ||
    (SHIP_COMBAT[s.cls].hull === SHIP_COMBAT[a.cls].hull && s.id < a.id)
      ? s
      : a,
  );
}

export const shipHull = (s: ShipState): number => s.hull ?? SHIP_COMBAT[s.cls].hull;

export interface FleetState {
  readonly id: number;
  readonly owner: string;
  readonly hex: Hex;
  readonly ships: readonly ShipState[];
  /** Hexes still to cross, next first. Empty = holding position. */
  readonly path: readonly Hex[];
  /** Ticks spent on the way to `path[0]`. */
  readonly progress: number;
  /** Scanning the dust cloud it sits in (GDD §7), or null. Moving cancels it. */
  readonly scan: ScanState | null;
  /** Combat stance (GDD §8); absent = aggressive. */
  readonly stance?: StanceId;
  /** Retreats home below this share of its hull; absent = COMBAT.defaultRetreatAt. */
  readonly retreatAt?: number;
  /** Heading home after a lost fight: moves even while engaged. */
  readonly retreating?: boolean;
}

export const fleetStance = (f: FleetState): StanceId => f.stance ?? 'aggressive';
export const fleetRetreatAt = (f: FleetState): number => f.retreatAt ?? COMBAT.defaultRetreatAt;
export const isArmed = (f: FleetState): boolean =>
  f.ships.some((s) => SHIP_COMBAT[s.cls].damage > 0);
/** Hull share left, 0..1. */
export const hullShare = (f: FleetState): number =>
  f.ships.reduce((a, s) => a + shipHull(s), 0) /
  f.ships.reduce((a, s) => a + SHIP_COMBAT[s.cls].hull, 0);

export interface BuildOrder {
  readonly ship: ShipClassId;
  readonly ticksLeft: number;
}

export function isShipClass(value: string): value is ShipClassId {
  return (SHIP_CLASS_IDS as readonly string[]).includes(value);
}

export function spaceportLevel(planet: PlanetState): number {
  return Math.max(0, ...planet.buildings.filter((b) => b.id === 'spaceport').map((b) => b.level));
}

const byLevel = (table: readonly number[], level: number) =>
  table[Math.min(table.length, Math.max(1, level)) - 1] ?? 0;

export const fleetLimit = (planet: PlanetState) =>
  byLevel(FLEET.limitBySpaceportLevel, spaceportLevel(planet));

export const parallelBuilds = (planet: PlanetState) =>
  spaceportLevel(planet) > 0 ? byLevel(FLEET.parallelBySpaceportLevel, spaceportLevel(planet)) : 0;

/** Hexes a fleet may enter: inside the map and not the star itself. */
export function isPassable(system: StarSystem, h: Hex): boolean {
  if (hexDistance(ORIGIN, h) > system.mapRadius) return false;
  return !system.starHexes.some((s) => s.q === h.q && s.r === h.r);
}

/**
 * Shortest path from `from` to `to` (excluding `from`), A* on the hex grid.
 * Ties break by hex key, so the same request always gives the same path.
 * Null when `to` cannot be reached.
 */
export function findPath(
  system: StarSystem,
  from: Hex,
  to: Hex,
  /** Hexes it may neither cross nor stop in (#158: an unarmed fleet and the enemy's ships). */
  blocked?: ReadonlySet<string>,
): Hex[] | null {
  if (!isPassable(system, to)) return null;
  if (blocked?.has(hexKey(to))) return null;
  const goal = hexKey(to);
  const start = hexKey(from);
  if (start === goal) return [];
  const came = new Map<string, Hex>();
  const cost = new Map<string, number>([[start, 0]]);
  // A small binary heap keyed by (f, key) keeps the order deterministic.
  const open: { f: number; key: string; hex: Hex }[] = [
    { f: hexDistance(from, to), key: start, hex: from },
  ];
  const less = (a: (typeof open)[number], b: (typeof open)[number]) =>
    a.f < b.f || (a.f === b.f && a.key < b.key);
  const push = (node: (typeof open)[number]) => {
    open.push(node);
    let i = open.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!less(open[i]!, open[p]!)) break;
      [open[i], open[p]] = [open[p]!, open[i]!];
      i = p;
    }
  };
  const pop = () => {
    const top = open[0]!;
    const last = open.pop()!;
    if (open.length) {
      open[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < open.length && less(open[l]!, open[m]!)) m = l;
        if (r < open.length && less(open[r]!, open[m]!)) m = r;
        if (m === i) break;
        [open[i], open[m]] = [open[m]!, open[i]!];
        i = m;
      }
    }
    return top;
  };

  while (open.length) {
    const { key, hex } = pop();
    if (key === goal) {
      const path: Hex[] = [];
      let k = key;
      let h: Hex | undefined = hex;
      while (k !== start && h) {
        path.push(h);
        h = came.get(k);
        k = h ? hexKey(h) : start;
      }
      return path.reverse();
    }
    const g = cost.get(key)!;
    for (const next of hexNeighbors(hex)) {
      if (!isPassable(system, next)) continue;
      if (blocked?.has(hexKey(next))) continue;
      const nk = hexKey(next);
      if (g + 1 >= (cost.get(nk) ?? Infinity)) continue;
      cost.set(nk, g + 1);
      came.set(nk, hex);
      push({ f: g + 1 + hexDistance(next, to), key: nk, hex: next });
    }
  }
  return null;
}

/** Ticks per hex of a fleet: its slowest ship. */
export function fleetTicksPerHex(fleet: FleetState): number {
  return Math.max(...fleet.ships.map((s) => SHIP_CLASSES[s.cls].ticksPerHex));
}

/** One tick of movement. `speed` < 1 for an unsupplied owner. */
export function moveFleet(fleet: FleetState, speed: number): FleetState {
  const next = fleet.path[0];
  if (!next) return fleet.progress === 0 ? fleet : { ...fleet, progress: 0 };
  const progress = fleet.progress + speed;
  const needed = fleetTicksPerHex(fleet);
  if (progress < needed) return { ...fleet, progress };
  return { ...fleet, hex: next, path: fleet.path.slice(1), progress: progress - needed };
}

export interface Upkeep {
  /** ГС; paid in the player's currency at the current rate. */
  readonly cash: number;
  readonly alloys: number;
  readonly energy: number;
}

/**
 * Upkeep of all a player's ships for one cycle: each ship's base upkeep
 * × N^exponent (N = ships owned), and × overLimitMultiplier for the ships
 * beyond the spaceport's fleet limit (the most recent ones).
 */
export function fleetUpkeep(ships: readonly ShipState[], limit: number): Upkeep {
  const n = ships.length;
  if (n === 0) return { cash: 0, alloys: 0, energy: 0 };
  const scale = n ** FLEET.upkeepExponent;
  const ordered = [...ships].sort((a, b) => a.id - b.id);
  let cash = 0;
  let alloys = 0;
  let energy = 0;
  ordered.forEach((s, i) => {
    const k = scale * (i >= limit ? FLEET.overLimitMultiplier : 1);
    const u = SHIP_CLASSES[s.cls].upkeep;
    cash += u.cash * k;
    alloys += u.alloys * k;
    energy += u.energy * k;
  });
  return { cash, alloys, energy };
}

export const shipsOf = (fleets: readonly FleetState[], owner: string): ShipState[] =>
  fleets.filter((f) => f.owner === owner).flatMap((f) => f.ships);
